import { capitalize } from '@shared/util';
import type { PaginationState } from '@tanstack/react-table';
import type { admin_financeadmin_Finance } from 'types/offers';
import type { PriceItemResponse } from 'types/price-data';

import { getCountries, getLanguages } from './country-language';

const countries = getCountries();
const langs = getLanguages();

export const getCountryName = (marketIsoCode: string): string =>
  countries.find((country) => country.code_2 === marketIsoCode)?.name ?? '';
export const getLanguageName = (locale: string): string =>
  langs.find((lang) => lang.iso639_1 === locale.split('-')[0])?.name[0] ?? '';

export const capitalizeFirstChar = (
  inputString: string = '',
  addSpaces = false,
): string => {
  const result = inputString.charAt(0).toUpperCase() + inputString.slice(1);

  if (addSpaces) {
    return result.replace(/([A-Z])/g, ' $1').substring(1);
  }

  return result;
};

export const getPluralName = (name: string) => {
  // Check if the name already ends with 's'
  if (name.endsWith('s')) {
    return name;
  }
  if (name.endsWith('y')) {
    return name.slice(0, -1) + 'ies';
  }
  return `${name}s`;
};

const getSalesModel = (obj: PriceItemResponse | admin_financeadmin_Finance) => {
  if (obj.salesModel?.toUpperCase() === 'WHOLESALE') {
    return 'Cash - Wholesale';
  }

  return (
    getSalesModelName(obj.salesModel ?? '') +
    ' - ' +
    ((obj as admin_financeadmin_Finance).businessModel
      ? capitalize((obj as admin_financeadmin_Finance).businessModel)
      : 'Direct')
  );
};

export const getFinanceKey = (
  obj: PriceItemResponse | admin_financeadmin_Finance,
) => `${getSalesModel(obj)} - ${obj.customerType?.toLowerCase()}`;

const salesModelsMap: Record<string, string> = {
  SUB: 'Subscription',
  CONTRACT_HIRE: 'Contract hire',
  SUB_FIXED: 'Subscription (fixed)',
  LOAN_WITH_BALLOON: 'Loan with balloon',
};

export const getSalesModelName = (salesModel = ''): string =>
  salesModelsMap[salesModel] || capitalize(salesModel);

export const deepEqual = (obj1: any, obj2: any): boolean => {
  // Check if both are the same reference
  if (obj1 === obj2) {
    return true;
  }

  // Check if both are objects
  if (
    obj1 == null ||
    obj2 == null ||
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object'
  ) {
    return false;
  }

  // Get the keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if the number of keys is the same
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Recursively check each key
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const debounce = (
  func: (...args: any[]) => void,
  delay = 300,
): ((...args: any[]) => void) & { cancel: () => void } => {
  let timeoutId: ReturnType<typeof setTimeout>;

  const debounced = (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };

  (debounced as ((...args: any[]) => void) & { cancel: () => void }).cancel =
    () => clearTimeout(timeoutId);

  return debounced as ((...args: any[]) => void) & { cancel: () => void };
};

export const get = (
  obj: any,
  path: string | string[] | number | symbol,
  defaultValue?: any,
): any => {
  if (!obj || (!path && path !== 0)) {
    return defaultValue;
  }

  const keys = Array.isArray(path) ? path : path.toString().split('.');
  let result = obj;

  for (const key of keys) {
    if (result == null || typeof result !== 'object') {
      return defaultValue;
    }
    result = result[key];
  }

  return result === undefined ? defaultValue : result;
};

export const sortBy = <T = any>(
  array: T[],
  iteratee: ((item: T) => any) | string,
): T[] => {
  if (!Array.isArray(array)) {
    return [];
  }

  const getValue =
    typeof iteratee === 'string' ? (item: T) => get(item, iteratee) : iteratee;

  return [...array].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);

    if (aValue < bValue) {
      return -1;
    }

    if (aValue > bValue) {
      return 1;
    }

    return 0;
  });
};

export const toLowerSnakeCase = (str: string | null | undefined): string =>
  (str || '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase();

export const toKebabCase = (str: string | null | undefined): string =>
  (str || '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();

const IGNORE_WORDS = new Set([
  'and',
  'all',
  'car',
  'for',
  'plan',
  'service',
  'test',
  'the',
  'with',
]);

const MIN_WORD_LENGTH = 4;

export const getCommonTags = (words: Array<string>, length = 10): string[] => {
  if (words.length === 0) {
    return [];
  }

  const wordFreq = new Map<string, number>();

  // Single-pass word extraction: build lowercase, validate inline
  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    if (!word) {
      continue;
    }

    let wordChars: string[] = [];
    let hasNonAlpha = false;

    for (let j = 0, len = word.length; j <= len; j++) {
      const char = word[j];
      const isSep = !char || char === ' ' || char === '-' || char === '_';

      if (isSep) {
        if (wordChars.length >= MIN_WORD_LENGTH && !hasNonAlpha) {
          const normalizedWord = wordChars.join('');

          if (!IGNORE_WORDS.has(normalizedWord)) {
            wordFreq.set(
              normalizedWord,
              (wordFreq.get(normalizedWord) || 0) + 1,
            );
          }
        }

        wordChars = [];
        hasNonAlpha = false;
      } else {
        const code = char.charCodeAt(0);

        if (code >= 65 && code <= 90) {
          wordChars.push(String.fromCharCode(code + 32));
        } else if (code >= 97 && code <= 122) {
          wordChars.push(char);
        } else {
          hasNonAlpha = true;
        }
      }
    }
  }

  if (wordFreq.size === 0) {
    return [];
  }

  // Avoid Array.from allocation: direct iteration for top-k
  const entries: Array<[string, number]> = [];
  wordFreq.forEach((count, word) => entries.push([word, count]));
  entries.sort((a, b) => b[1] - a[1]);

  const result: string[] = [];

  for (let i = 0; i < Math.min(length, entries.length); i++) {
    result.push(entries[i][0]);
  }

  return result;
};

export const isUuid = (code?: null | string): boolean =>
  Boolean(
    code &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        code,
      ),
  );

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// strips accents/diacritics so e.g. "café" matches a search for "cafe"
export const normalizeSearchText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

// AND-matches whitespace-separated query terms against a normalized haystack, in any order
export const createSearchMatcher = (query: string) => {
  const terms = normalizeSearchText(query)
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => new RegExp(escapeRegExp(term)));

  return (haystack: string): boolean =>
    terms.every((term) => term.test(haystack));
};

const findScrollableParent = (element: HTMLElement | null) => {
  let currentElement = element?.parentElement ?? null;

  while (currentElement) {
    const { overflowY } = window.getComputedStyle(currentElement);
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      currentElement.scrollHeight > currentElement.clientHeight;

    if (canScroll) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
};

export const scrollToBottom = (element: HTMLElement | null) => {
  const scrollContainer = findScrollableParent(element);

  if (scrollContainer) {
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: 'smooth',
    });
    return;
  }

  window.scrollTo({
    top: Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
    ),
    behavior: 'smooth',
  });
};

const SPECIFICATION_CODE_RANK = new Map<string, number>([
  ['version', 0],
  ['trim', 1],
  ['driveline', 2],
  ['theme', 3],
  ['color', 4],
  ['wheel', 5],
  ['steering', 6],
  ['seats', 7],
  ['upholstery', 8],
]);

const DEFAULT_SPECIFICATION_RANK = Number.MAX_SAFE_INTEGER;

export const sortSpecifications = <
  T extends { code?: string | null } | { specificationCode?: string | null },
>(
  specs?: Array<T> | null,
): Array<T> => {
  if (!specs?.length) {
    return [];
  }

  return specs
    .map((spec, index) => {
      const code =
        'code' in spec
          ? spec.code
          : 'specificationCode' in spec
            ? spec.specificationCode
            : null;

      return {
        spec,
        index,
        rank:
          SPECIFICATION_CODE_RANK.get(code?.toLowerCase() ?? '') ??
          DEFAULT_SPECIFICATION_RANK,
      };
    })
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ spec }) => spec);
};

export const getLimitOffsetFromPagination = (pagination: PaginationState) => {
  const limit = pagination.pageSize ?? 10;
  const offset = (pagination.pageIndex ?? 0) * limit;
  return { limit, offset };
};

export const toMap = <T = unknown, K extends keyof T = keyof T>(
  arr: Array<T>,
  aggregatorKey: K,
): Map<T[K], T> => new Map(arr.map((item) => [item[aggregatorKey], item]));
